import { createSignal, onMount, untrack, For, Show } from 'solid-js';
import { createStore } from 'solid-js/store';
import { parseNumericInput } from '../lib/money.js';

// RekapLembur — the overtime (Lembur SPL) review surface for a draft run, the
// sibling of RekapAbsensi. It derives each employee's SPL-gated overtime from
// the approved authorizations (pay_overtime) + the real clock-out and shows
// only the exceptions worth reviewing: who actually has SPL days, and how the
// hours split into Jam1 (first-hour count) / Jam2 (remainder). One click pushes
// the reviewed numbers into the run's input grid; each cell stays editable.
//
// The panel hides itself for schemes that declare no overtime inputs (SALES,
// MANAGERIAL) — those return zero columns from the endpoint.

async function getJSON(url) {
  const res = await fetch(url);
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || ('HTTP ' + res.status));
  return res.json();
}

async function postJSON(url, body) {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  return { ok: res.ok, data };
}

// Integer quarter-hours → trimmed decimal-hours string ("6" → "1.5").
const qToHours = (q) => {
  const whole = Math.floor(q / 4);
  return [whole, whole + '.25', whole + '.5', whole + '.75'][q % 4];
};

export default function RekapLembur(props) {
  // props: runId (number), onApplied (fn) — refetch the run detail after push.
  const [payload, setPayload] = createStore({ loaded: false, columns: [], rows: [], noSplCount: 0 });
  const [drill, setDrill] = createStore({}); // nik → { open, loading, agg }
  const [loading, setLoading] = createSignal(false);
  const [applying, setApplying] = createSignal(false);
  // One-shot guard: after a successful apply the button stays disabled so an
  // accidental second click can't re-overwrite manual per-period edits. The
  // state is stored ON THE RUN (pay_run.overtime_applied_at, migration 047) so
  // it holds across tabs/browsers; Muat ulang explicitly unlocks it. A local
  // cell edit re-arms only this tab — the server then asks for confirmation.
  const [applied, setApplied] = createSignal(false);
  const [msg, setMsg] = createSignal(null);
  const [dept, setDept] = createSignal('');
  const [search, setSearch] = createSignal('');

  async function load() {
    setLoading(true);
    setMsg(null);
    try {
      const d = await getJSON(`/api/run/${props.runId}/overtime`);
      setPayload({ loaded: true, columns: d.columns ?? [], rows: d.rows ?? [], noSplCount: d.noSplCount ?? 0, viaProc: d.viaProc || '' });
      setApplied(!!d.appliedAt); // server truth — consistent across tabs
      setDrill({});
    } catch (e) {
      setPayload('loaded', true);
      setMsg({ ok: false, msg: e.message });
    }
    setLoading(false);
  }
  // Auto-load once: cheap when the scheme has no overtime inputs (the endpoint
  // returns no columns before touching any employee).
  onMount(load);

  function setCell(i, code, v) {
    setPayload('rows', i, 'values', code, v);
    setApplied(false); // edited values differ from what was applied
  }

  async function toggleDrill(nik) {
    const cur = drill[nik];
    if (cur?.open) { setDrill(nik, 'open', false); return; }
    if (cur?.agg) { setDrill(nik, 'open', true); return; }
    setDrill(nik, { open: true, loading: true, agg: null });
    try {
      const agg = await getJSON(`/api/run/${props.runId}/overtime/${encodeURIComponent(nik)}`);
      setDrill(nik, { open: true, loading: false, agg });
    } catch (e) {
      setDrill(nik, { open: true, loading: false, agg: null, error: e.message });
    }
  }

  async function apply() {
    setApplying(true);
    setMsg(null);
    const cells = [];
    for (const row of payload.rows) {
      for (const c of payload.columns) {
        cells.push({ nik: row.nik, code: c.code, value: String(row.values[c.code] ?? '') });
      }
    }
    let r = await postJSON(`/api/run/${props.runId}/overtime/apply`, { cells });
    // Another tab already applied (server-side guard): offer a deliberate
    // unlock + retry instead of a dead-end error.
    if (!r.ok && r.data.alreadyApplied
        && confirm('Nilai sudah pernah diterapkan (mungkin dari tab lain). Terapkan lagi dan timpa nilai di input per periode?')) {
      await postJSON(`/api/run/${props.runId}/overtime/unlock`, {});
      r = await postJSON(`/api/run/${props.runId}/overtime/apply`, { cells });
    }
    setApplying(false);
    if (r.ok) {
      setApplied(true);
      setMsg({ ok: true, msg: `${r.data.written} nilai lembur dikirim ke input per periode.` });
      props.onApplied?.();
    } else setMsg({ ok: false, msg: r.data.error });
  }

  // Muat ulang: explicitly re-arm the server-side guard, then reload.
  async function muatUlang() {
    await postJSON(`/api/run/${props.runId}/overtime/unlock`, {});
    load();
  }

  // ── Sorting (click a header) ──────────────────────────────────────────
  // Each wrapper keeps the ORIGINAL payload index so setCell(i,…) stays correct
  // after sorting. The comparator reads r.values UNTRACKED so typing in a sorted
  // value column doesn't re-sort and jump the row.
  const [sort, setSort] = createSignal({ key: '', dir: 'asc' });
  function toggleSort(key) {
    const s = sort();
    setSort(s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' });
  }
  const arrow = (key) => (sort().key === key ? (sort().dir === 'asc' ? ' ▲' : ' ▼') : '');
  function sortVal(r, key) {
    if (key === 'spl') return r.splDays || 0;
    if (key === 'total') return parseFloat(r.totalHours) || 0;
    if (key.startsWith('val:')) return parseFloat(r.values[key.slice(4)]) || 0;
    return r.name || ''; // 'name'
  }
  const departments = () => {
    const s = new Set();
    for (const r of payload.rows) if (r.department) s.add(r.department);
    return [...s].sort();
  };
  const filteredRows = () => {
    const d = dept(), kw = search().trim().toLowerCase();
    return payload.rows
      .map((r, i) => ({ r, i }))
      .filter(({ r }) => (!d || r.department === d) &&
        (!kw || (r.name || '').toLowerCase().includes(kw) || (r.nik || '').toLowerCase().includes(kw)));
  };
  const sortedRows = () => {
    const arr = filteredRows();
    const s = sort();
    if (!s.key) return arr;
    const mul = s.dir === 'asc' ? 1 : -1;
    untrack(() => arr.sort((a, b) => {
      const av = sortVal(a.r, s.key), bv = sortVal(b.r, s.key);
      return (typeof av === 'number' && typeof bv === 'number')
        ? (av - bv) * mul : String(av).localeCompare(String(bv)) * mul;
    }));
    return arr;
  };

  const viaProc = () => payload.viaProc || '';
  // Hidden until we know the scheme has overtime inputs (avoids flashing an
  // empty panel for SALES/MANAGERIAL).
  const show = () => payload.loaded && (payload.columns.length > 0 || !!msg());

  return (
    <Show when={show()}>
      <div class="card card-pad ab-rekap ot-rekap">
        <div class="ky-vars-head">
          <h4 class="kg-hist-title">Rekap Lembur{viaProc() ? '' : ' (SPL)'}</h4>
          <span class="kg-meta-text" style="margin-left:8px">
            {viaProc() ? `dihitung oleh stored procedure: ${viaProc()}` : 'dari SPL disetujui + jam pulang nyata'}
          </span>
          <div class="spacer" />
          <button class="btn ghost" disabled={loading()} onClick={muatUlang}>{loading() ? 'Memuat…' : 'Muat ulang'}</button>
        </div>

        <p class="ky-vars-note">
          Jam lembur dihitung per hari SPL: <b>min(pulang, selesai SPL) − mulai SPL</b>,
          dibulatkan ke ¼ jam. <b>Jam 1</b> = jumlah hari lembur &gt; 1 jam;
          <b> Jam 2</b> = sisa jamnya. Tinjau, sesuaikan bila perlu, lalu kirim ke
          input per periode.
        </p>

        <Show when={msg()}>
          <div class={`kg-verdict ${msg().ok ? 'ok' : 'fail'}`} style="margin:8px 0">{msg().msg}</div>
        </Show>

        <Show when={payload.columns.length > 0}>
          <div class="ab-rekap-summary kg-meta-text">
            <Show when={payload.rows.length > 0} fallback={<span>Tidak ada karyawan dengan lembur pada periode ini.</span>}>
              <span class="badge info">{payload.rows.length} karyawan dengan lembur</span>
            </Show>
            <Show when={payload.noSplCount > 0}>
              <span class="kg-meta-text">{payload.noSplCount} tanpa lembur</span>
            </Show>
          </div>

          <Show when={payload.rows.length > 0}>
            <div class="lb-filter">
              <label class="kg-field"><span>Departemen</span>
                <select class="kg-select" value={dept()} onInput={(e) => setDept(e.currentTarget.value)}>
                  <option value="">Semua departemen</option>
                  <For each={departments()}>{(d) => <option value={d}>{d}</option>}</For>
                </select>
              </label>
              <label class="kg-field lb-search"><span>Cari nama / NIK</span>
                <input type="text" placeholder="kata kunci…" value={search()} onInput={(e) => setSearch(e.currentTarget.value)} />
              </label>
              <Show when={dept() || search()}>
                <button class="btn ghost" onClick={() => { setDept(''); setSearch(''); }}>Reset</button>
              </Show>
            </div>
            <div class="pg-grid-wrap">
              <table class="data pg-grid">
                <thead>
                  <tr>
                    <th class="pg-sticky sortable" onClick={() => toggleSort('name')}>Karyawan{arrow('name')}</th>
                    <th class="num sortable" onClick={() => toggleSort('spl')}>SPL{arrow('spl')}</th>
                    <For each={payload.columns}>{(c) =>
                      <th class="num sortable" title={c.label} onClick={() => toggleSort('val:' + c.code)}>{c.code}{arrow('val:' + c.code)}</th>}
                    </For>
                    <th class="num sortable" onClick={() => toggleSort('total')}>Total jam{arrow('total')}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <For each={sortedRows()}>{({ r, i }) =>
                    <>
                      <tr classList={{ 'ab-row-sticky': drill[r.nik]?.open }}>
                        <td class="pg-sticky">
                          <div class="kg-code">{r.nik}</div>
                          <div class="kg-sub">{r.name}</div>
                        </td>
                        <td class="num">
                          <Show when={!viaProc()} fallback={<span class="vg-dim">—</span>}>
                            <span class="badge info">{r.splDays} hari</span>
                            <Show when={r.noFid}><span class="badge danger" title="Tanpa sidik jari — pulang dianggap mentok di selesai SPL">no FID</span></Show>
                          </Show>
                        </td>
                        <For each={payload.columns}>{(c) =>
                          <td class="num">
                            <OTCell value={r.values[c.code] ?? ''} onSave={(v) => setCell(i, c.code, v)} />
                          </td>
                        }</For>
                        <td class="num kg-meta-text">{viaProc() ? '' : `${r.totalHours} jam`}</td>
                        <td class="num">
                          <Show when={!viaProc()}>
                            <button class="btn ghost" onClick={() => toggleDrill(r.nik)}>
                              {drill[r.nik]?.open ? 'tutup' : 'rincian'}
                            </button>
                          </Show>
                        </td>
                      </tr>
                      <Show when={drill[r.nik]?.open}>
                        <tr class="ab-drill-row">
                          <td colspan={payload.columns.length + 4}>
                            <Show when={drill[r.nik]?.loading}><span class="kg-meta-text">memuat rincian…</span></Show>
                            <Show when={drill[r.nik]?.error}><span class="ferr">⚠ {drill[r.nik].error}</span></Show>
                            <Show when={drill[r.nik]?.agg}>
                              <SPLTable agg={drill[r.nik].agg} />
                            </Show>
                          </td>
                        </tr>
                      </Show>
                    </>
                  }</For>
                </tbody>
              </table>
            </div>

            <div class="ab-rekap-foot">
              <span class="kg-meta-text">Angka tersimpan ke kolom input per periode di bawah; tetap bisa diubah manual.</span>
              <div class="spacer" />
              <button class="btn primary" disabled={applying() || applied()} onClick={apply}
                title={applied() ? 'Sudah diterapkan — klik Muat ulang untuk mengaktifkan kembali' : undefined}>
                {applying() ? 'Mengirim…' : applied() ? 'Sudah diterapkan ✓' : 'Terapkan ke input per periode'}
              </button>
            </div>
          </Show>
        </Show>
      </div>
    </Show>
  );
}

// OTCell — editable numeric cell; commits on every input when changed.
function OTCell(props) {
  const [val, setVal] = createSignal(props.value);
  return (
    <input
      class="pg-cell pg-cell-num"
      classList={{ filled: val() !== '' && val() !== '0' }}
      type="text"
      inputmode="decimal"
      value={val()}
      onInput={(e) => { setVal(e.currentTarget.value); props.onSave(parseNumericInput(e.currentTarget.value)); }}
      onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
    />
  );
}

// SPLTable — the SPL-day-by-day evidence behind one employee's Jam1/Jam2.
function SPLTable(props) {
  return (
    <table class="data ab-days">
      <thead>
        <tr>
          <th>Tanggal</th><th>SPL mulai</th><th>SPL selesai</th><th>Pulang</th>
          <th class="num">Jam</th><th></th><th>Penjelasan</th>
        </tr>
      </thead>
      <tbody>
        <For each={props.agg.days ?? []}>{(d) =>
          <tr>
            <td class="ky-nik">{d.date}</td>
            <td class="ky-nik">{d.splStart || '—'}</td>
            <td class="ky-nik">{d.splEnd || '—'}</td>
            <td class="ky-nik">{d.clockOut || '—'}</td>
            <td class="num">{qToHours(d.quarters)}</td>
            <td><Show when={d.quarters > 4}><span class="badge success" title="Lembur > 1 jam (masuk hitungan Jam 1)">jam 1</span></Show></td>
            <td class="kg-meta-text">{d.explain}</td>
          </tr>
        }</For>
        <Show when={(props.agg.days ?? []).length === 0}>
          <tr><td colspan="7" class="kg-meta-text">Tidak ada hari SPL.</td></tr>
        </Show>
      </tbody>
    </table>
  );
}
